/* eslint-disable default-case */
import React, { useState, useMemo } from 'react';
import { CalendarOff, Check, AlertTriangle, Calendar } from 'lucide-react';
import { getUsedDaysByLeaveYear, getLeaveYear } from '../utils/vacationUtils';
import DatePicker, { registerLocale } from 'react-datepicker';
import el from 'date-fns/locale/el';
import 'react-datepicker/dist/react-datepicker.css';

registerLocale('el', el);

const ManageDaysView = ({ currentUser, userDatabase, departments, onGrantDays, vacationRequests, annualBalances = [], holidays = [], isViewer = false }) => {
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [reason, setReason] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [usersExceedingLimit, setUsersExceedingLimit] = useState([]);
  const [grantType, setGrantType] = useState('actual');

  const currentLeaveYear = useMemo(() => getLeaveYear(new Date()), []);

  const allEmployees = userDatabase;

  const dateToISOString = (date) => {
    if (!date) return '';
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  };

  const formatDate = (date) => {
    if (!date) return '';
    return date.toLocaleDateString('el-GR', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  const isWeekend = (date) => {
    const day = date.getDay();
    return day === 0 || day === 6;
  };

  const isPublicHoliday = (date) => {
    if (!holidays || !Array.isArray(holidays)) return false;
    const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    return holidays.some(h => {
      const hDate = new Date(h.holiday_date);
      const holidayStr = `${hDate.getFullYear()}-${String(hDate.getMonth() + 1).padStart(2, '0')}-${String(hDate.getDate()).padStart(2, '0')}`;
      return holidayStr === dateStr;
    });
  };

  const calculateDays = () => {
    if (!startDate || !endDate) return 0;
    const start = startDate instanceof Date ? startDate : new Date(startDate);
    const end = endDate instanceof Date ? endDate : new Date(endDate);
    if (end < start) return 0;

    let workingDays = 0;
    const currentDate = new Date(start);

    while (currentDate <= end) {
      if (!isWeekend(currentDate) && !isPublicHoliday(currentDate)) {
        workingDays++;
      }
      currentDate.setDate(currentDate.getDate() + 1);
    }
    return workingDays;
  };

  const days = calculateDays();

  // Helper to calculate previous year overflow (debt)
  const getOverflowFromPrevYear = (userId, targetYear) => {
    if (!annualBalances || annualBalances.length === 0) return 0;
    const prevYear = targetYear - 1;
    const pivotedEntry = annualBalances.find(b => Number(b.userId || b.user_id) === Number(userId));
    
    let prevTotalAllowed = 0;
    const prevYearKey = `balance_${prevYear}`;
    
    if (pivotedEntry && pivotedEntry[prevYearKey] !== undefined) {
      prevTotalAllowed = Number(pivotedEntry[prevYearKey]);
    } else {
      const rawEntry = annualBalances.find(b => Number(b.cycle_start_year || b.year) === prevYear);
      if (rawEntry && rawEntry.total_allowed !== undefined) {
        prevTotalAllowed = Number(rawEntry.total_allowed);
      }
    }

    if (prevTotalAllowed <= 0) return 0;

    const prevUsedDays = vacationRequests 
      ? getUsedDaysByLeaveYear(userId, vacationRequests, prevYear, holidays) 
      : 0;
      
    return Math.max(0, prevUsedDays - prevTotalAllowed);
  };

  const userBalanceMap = useMemo(() => {
    const map = new Map();
    const targetYear = currentLeaveYear;

    allEmployees.forEach(user => {
      const userUsedDays = vacationRequests ? getUsedDaysByLeaveYear(user.id, vacationRequests, targetYear, holidays) : 0;

      let userTotalDays = 0;
      if (annualBalances && annualBalances.length > 0) {
        const pivotedEntry = annualBalances.find(b => Number(b.userId || b.user_id) === Number(user.id));
        const yearKey = `balance_${targetYear}`;
        if (pivotedEntry && pivotedEntry[yearKey] !== undefined) {
          userTotalDays = Number(pivotedEntry[yearKey]);
        } else {
          const rawEntry = annualBalances.find(b => Number(b.cycle_start_year || b.year) === targetYear);
          if (rawEntry && rawEntry.total_allowed !== undefined) {
            userTotalDays = Number(rawEntry.total_allowed);
          }
        }
      }

      const overflowFromPrev = getOverflowFromPrevYear(user.id, targetYear);
      const userRemainingDays = Math.max(0, userTotalDays - userUsedDays - overflowFromPrev);

      map.set(user.id, { userUsedDays, userTotalDays, overflowFromPrev, userRemainingDays });
    });

    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allEmployees, vacationRequests, annualBalances, holidays, currentLeaveYear]);

  if (currentUser.role !== 'manager' && !isViewer) {
    return null;
  }

  const handleUserToggle = (userId) => {
    setSelectedUsers(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAll = () => {
    if (selectedUsers.length === allEmployees.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(allEmployees.map(u => u.id));
    }
  };

  const handleSubmit = async () => {
    if (selectedUsers.length === 0) {
      alert('Παρακαλώ επιλέξτε τουλάχιστον έναν υπάλληλο');
      return;
    }

    if (!startDate || !endDate) {
      alert('Παρακαλώ επιλέξτε ημερομηνίες έναρξης και λήξης');
      return;
    }

    if (days <= 0) {
      alert('Παρακαλώ επιλέξτε έγκυρο εύρος ημερομηνιών');
      return;
    }

    const grantStart = dateToISOString(startDate);
    const grantEnd   = dateToISOString(endDate);
    

    const usersWithOverlap = [];
    selectedUsers.forEach(userId => {
        const user = allEmployees.find(u => u.id === userId);
        if (!user) return;
        const hasOverlap = (vacationRequests || []).some(req => {
          const reqUserId = req.user_id || req.userId;
          if (reqUserId !== userId) return false;
          if (!['approved', 'pending', 'manager1_approved'].includes(req.status)) return false;
          const reqStart = req.start_date || req.startDate;
          const reqEnd   = req.end_date   || req.endDate;
          if (!reqStart || !reqEnd) return false;
          return reqStart <= grantEnd && reqEnd >= grantStart;
        });
        if (hasOverlap) usersWithOverlap.push(user.name);
    });

    if (usersWithOverlap.length > 0) {
      alert(
        `Οι παρακάτω υπάλληλοι έχουν ήδη αίτηση σε αυτές τις ημερομηνίες:\n` +
        usersWithOverlap.join('\n')
      );
      return;
    }

    if (!reason.trim()) {
      alert('Παρακαλώ εισάγετε αιτιολογία');
      return;
    }

    const usersWithIssues = [];
    // eslint-disable-next-line no-unused-vars
    const targetYear = currentLeaveYear;

    if (grantType !== 'unpaid') {
      selectedUsers.forEach(userId => {
        const user = allEmployees.find(u => u.id === userId);
        if (user) {
          const balance = userBalanceMap.get(user.id);
          const userRemainingDays = balance ? balance.userRemainingDays : 0;

          if (days > userRemainingDays) {
            usersWithIssues.push({
              name: user.name,
              remaining: userRemainingDays,
              exceeding: days - userRemainingDays
            });
          }
        }
      });
    }

    if (usersWithIssues.length > 0) {
      setUsersExceedingLimit(usersWithIssues);
      setShowWarningModal(true);
      return;
    }

    await proceedWithGrant();
  };

  const proceedWithGrant = async () => {
    const result = await onGrantDays({
      userIds: selectedUsers,
      days: days,
      startDate: dateToISOString(startDate),
      endDate: dateToISOString(endDate),
      reason: reason.trim(),
      isDeduction: true,
      leaveType: grantType
    });

    if (result.success) {
      setShowSuccess(true);
      setSelectedUsers([]);
      setStartDate(null);
      setEndDate(null);
      setReason('');
      setGrantType('actual');
      setShowWarningModal(false);
      setUsersExceedingLimit([]);
      setTimeout(() => setShowSuccess(false), 3000);
    } else {
      alert(result.error || 'Αποτυχία καταχώρησης');
    }
  };

  const getDepartmentById = (deptId) => {
    return departments.find(d => d.id === deptId);
  };

  const groupedEmployees = departments.map(dept => ({
    department: dept,
    employees: allEmployees.filter(u => (u.department_id || u.departmentId) === dept.id)
  })).filter(group => group.employees.length > 0);
  
  return (
    <div>
      <div className="flex items-center justify-between mb-6 xs:mb-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-800 dark:text-jewel-text xs:text-sm">Καταχώρηση Υποχρεωτικής Άδειας</h2>
        </div>
        <CalendarOff className="h-8 w-8 xs:h-6 xs:w-6 text-blue-600 dark:text-jewel-teal-tint" />
      </div>
      {showSuccess && (
        <div className="mb-4 p-4 xs:p-3 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg flex items-center space-x-2 text-green-800 dark:text-green-300">
          <Check className="h-5 w-5 xs:h-4 xs:w-4 flex-shrink-0" />
          <span className="text-sm xs:text-xs">Η άδεια καταχωρήθηκε επιτυχώς!</span>
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 xs:gap-4">
        <div className="lg:col-span-2">
          <div className="bg-white dark:bg-jewel-bg rounded-lg border border-gray-200 dark:border-jewel-border p-4 xs:p-3">
            <div className="flex items-center justify-between mb-4 xs:mb-3">
              <h3 className="font-semibold text-gray-800 dark:text-jewel-text xs:text-sm">
                Επιλογή Υπαλλήλων ({selectedUsers.length})
              </h3>
              <button
                onClick={handleSelectAll}
                className="px-3 py-1 xs:px-2 xs:py-0.5 text-sm xs:text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
              >
                {selectedUsers.length === allEmployees.length ? 'Απεπιλογή όλων' : 'Επιλογή όλων'}
              </button>
            </div>
            <div className="space-y-4 xs:space-y-3 max-h-[60vh] overflow-y-auto">
              {groupedEmployees.map(({ department, employees }) => (
                <div key={department.id}>
                  <div className="flex items-center space-x-2 mb-2">
                    <div
                      className="w-3 h-3 xs:w-2 xs:h-2 rounded-full"
                      style={{ backgroundColor: department.color }}
                    ></div>
                    <h4 className="font-medium text-gray-700 dark:text-jewel-text text-sm xs:text-xs">{department.name}</h4>
                    <span className="text-xs xs:text-[10px] text-gray-500 dark:text-jewel-text-muted">
                      ({employees.length})
                    </span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {employees.map(user => {
                      const userDept = getDepartmentById(user.department_id || user.departmentId);
                      const isSelected = selectedUsers.includes(user.id);
                      const balance = userBalanceMap.get(user.id) || { userRemainingDays: 0 };
                      const { userRemainingDays } = balance;

                      return (
                        <label
                          key={user.id}
                          className={`group relative flex items-center space-x-3 xs:space-x-2 p-3 xs:p-2 rounded-lg border-2 cursor-pointer transition-all ${
                            isSelected
                              ? 'border-blue-500 bg-blue-50 dark:bg-jewel-teal/20'
                              : 'border-gray-200 dark:border-jewel-border hover:border-gray-300 dark:hover:border-gray-600'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleUserToggle(user.id)}
                            className="w-4 h-4 xs:w-3 xs:h-3 text-blue-600 rounded"
                          />
                          <div
                            className="w-8 h-8 xs:w-6 xs:h-6 rounded-full flex items-center justify-center text-white font-semibold text-xs xs:text-[10px] flex-shrink-0"
                            style={{ backgroundColor: userDept?.color || '#3B82F6' }}
                          >
                            {user.name.split(' ').map(n => n[0]).join('')}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-800 dark:text-jewel-text text-sm xs:text-xs truncate">
                              {user.name}
                            </p>
                            <p className="text-xs xs:text-[10px] text-gray-500 dark:text-jewel-text-muted truncate">
                              {user.email}
                            </p>
                          </div>                         
                          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-1.5 bg-gray-900 dark:bg-jewel-surface text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
                            Διαθέσιμες ({currentLeaveYear}): {userRemainingDays} {userRemainingDays === 1 ? 'ημέρα' : 'ημέρες'}
                            <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900 dark:border-t-gray-700"></div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="lg:col-span-1">
          <div className="bg-white dark:bg-jewel-bg rounded-lg border border-gray-200 dark:border-jewel-border p-4 xs:p-3 space-y-4 xs:space-y-3 sticky top-4">
            <h3 className="font-semibold text-gray-800 dark:text-jewel-text xs:text-sm">Λεπτομέρειες Άδειας</h3>
            <div className="space-y-3">
              <div>
                <label className="text-sm xs:text-xs font-medium text-gray-700 dark:text-jewel-text mb-2 flex items-center space-x-1">
                  <Calendar className="h-4 w-4 xs:h-3 xs:w-3" />
                  <span>Ημερομηνία Έναρξης *</span>
                </label>
                <DatePicker
                  selected={startDate}
                  onChange={(date) => setStartDate(date)}
                  locale="el"
                  dateFormat="dd/MM/yyyy"
                  placeholderText="Επιλέξτε ημερομηνία"
                  className="w-full px-3 py-2 xs:px-2 xs:py-1.5 text-sm xs:text-xs border border-gray-300 dark:border-jewel-border bg-white dark:bg-jewel-surface text-gray-900 dark:text-jewel-text rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  wrapperClassName="w-full"
                />
              </div>             
              <div>
                <label className="text-sm xs:text-xs font-medium text-gray-700 dark:text-jewel-text mb-2 flex items-center space-x-1">
                  <Calendar className="h-4 w-4 xs:h-3 xs:w-3" />
                  <span>Ημερομηνία Λήξης *</span>
                </label>
                <DatePicker
                  selected={endDate}
                  onChange={(date) => setEndDate(date)}
                  minDate={startDate}
                  locale="el"
                  dateFormat="dd/MM/yyyy"
                  placeholderText="Επιλέξτε ημερομηνία"
                  className="w-full px-3 py-2 xs:px-2 xs:py-1.5 text-sm xs:text-xs border border-gray-300 dark:border-jewel-border bg-white dark:bg-jewel-surface text-gray-900 dark:text-jewel-text rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  wrapperClassName="w-full"
                />
              </div>
              {startDate && endDate && days > 0 && (
                <div className="p-3 bg-blue-50 dark:bg-jewel-teal/20 border border-blue-200 dark:border-jewel-teal rounded-lg">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-700 dark:text-jewel-text">Συνολικές Ημέρες:</span>
                    <span className="font-bold text-blue-600 dark:text-jewel-teal-tint">{days} {days === 1 ? 'ημέρα' : 'ημέρες'}</span>
                  </div>
                  <div className="mt-2 pt-2 border-t border-blue-200 dark:border-jewel-teal">
                    <p className="text-xs text-gray-600 dark:text-jewel-text-muted">
                      {formatDate(startDate)} - {formatDate(endDate)}
                    </p>
                  </div>
                </div>
              )}
            </div>
            <div>
              <label className="text-sm xs:text-xs font-medium text-gray-700 dark:text-jewel-text mb-2 block">
                Τύπος Καταχώρησης *
              </label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setGrantType('actual')}
                  className={`px-3 py-2 xs:px-2 xs:py-1.5 text-sm xs:text-xs rounded-lg border-2 font-medium transition-all ${
                  grantType === 'actual'
                  ? 'border-blue-500 bg-blue-50 dark:bg-jewel-teal/20 text-blue-700 dark:text-jewel-teal-tint'
                  : 'border-gray-200 dark:border-jewel-border text-gray-600 dark:text-jewel-text hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  Κανονικές Ημέρες
                </button>
                <button
                  type="button"
                  onClick={() => setGrantType('unpaid')}
                  className={`px-3 py-2 xs:px-2 xs:py-1.5 text-sm xs:text-xs rounded-lg border-2 font-medium transition-all ${
                  grantType === 'unpaid'
                  ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300'
                  : 'border-gray-200 dark:border-jewel-border text-gray-600 dark:text-jewel-text hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  Άνευ Αποδοχών
                </button>
              </div>
              <p className="text-xs text-gray-500 dark:text-jewel-text-muted mt-1">
                {grantType === 'unpaid'
                ? 'Δεν θα αφαιρεθεί από το υπόλοιπο κανονικής άδειας.'
                : 'Θα αφαιρεθεί από το υπόλοιπο κανονικής άδειας.'}
              </p>
            </div>
            <div>
              <label className="block text-sm xs:text-xs font-medium text-gray-700 dark:text-jewel-text mb-2">
                Αιτιολογία *
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="π.χ. Κλείσιμο εταιρείας, Εθνική αργία, Έκτακτη συντήρηση κτιρίου"
                rows="4"
                className="w-full px-3 py-2 xs:px-2 xs:py-1.5 text-sm xs:text-xs border border-gray-300 dark:border-jewel-border bg-white dark:bg-jewel-surface text-gray-900 dark:text-jewel-text rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
              />
              <p className="text-xs text-gray-500 dark:text-jewel-text-muted mt-1">* Υποχρεωτικό πεδίο</p>
            </div>
            <div className="pt-2 border-t border-gray-200 dark:border-jewel-border">
              <div className="flex justify-between text-sm xs:text-xs mb-2">
                <span className="text-gray-600 dark:text-jewel-text">Επιλεγμένοι:</span>
                <span className="font-bold text-blue-600 dark:text-jewel-teal-tint">{selectedUsers.length} υπάλληλοι</span>
              </div>
              <div className="flex justify-between text-sm xs:text-xs mb-4">
                <span className="text-gray-600 dark:text-jewel-text">Σύνολο ημερών:</span>
                <span className="font-bold text-orange-600 dark:text-orange-400">
                  {selectedUsers.length * days}
                </span>
              </div>
            </div>
            <button
              onClick={handleSubmit}
              disabled={selectedUsers.length === 0 || !startDate || !endDate || days <= 0 || !reason.trim()}
              className="w-full px-4 py-2 xs:px-3 xs:py-1.5 text-sm xs:text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 dark:disabled:bg-gray-700 disabled:cursor-not-allowed transition-colors flex items-center justify-center space-x-2"
            >
              <Check className="h-4 w-4 xs:h-3 xs:w-3" />
              <span>Καταχώρηση Άδειας</span>
            </button>
          </div>
        </div>
      </div>
      {showWarningModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-jewel-bg rounded-lg p-6 w-full max-w-md max-h-[80vh] overflow-y-auto">
            <div className="flex items-center space-x-3 mb-4">
              <AlertTriangle className="h-6 w-6 text-orange-500" />
              <h3 className="text-lg font-semibold text-gray-800 dark:text-jewel-text">Προειδοποίηση Υπέρβασης</h3>
            </div>           
            <p className="text-sm text-gray-600 dark:text-jewel-text mb-4">
              Οι παρακάτω υπάλληλοι δεν έχουν αρκετές διαθέσιμες ημέρες (υπόλοιπο {currentLeaveYear}):
            </p>
            <div className="space-y-3 mb-6">
              {usersExceedingLimit.map((user, index) => (
                <div key={index} className="p-3 bg-orange-50 dark:bg-orange-900/30 border border-orange-200 dark:border-orange-800 rounded-lg">
                  <p className="font-medium text-gray-800 dark:text-jewel-text text-sm">{user.name}</p>
                  <p className="text-xs text-gray-600 dark:text-jewel-text-muted mt-1">
                    Διαθέσιμες: {user.remaining} ημέρες
                  </p>
                  <p className="text-xs text-orange-700 dark:text-orange-400 font-semibold mt-1">
                    Υπέρβαση: {user.exceeding} ημέρες
                  </p>
                </div>
              ))}
            </div>
            <p className="text-sm text-gray-700 dark:text-jewel-text mb-6">
              Είστε σίγουροι ότι θέλετε να συνεχίσετε; Αυτό θα δημιουργήσει αρνητικό υπόλοιπο.
            </p>
            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowWarningModal(false);
                  setUsersExceedingLimit([]);
                }}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-jewel-border text-gray-700 dark:text-jewel-text rounded-lg hover:bg-gray-50 dark:hover:bg-jewel-surface transition-colors"
              >
                Ακύρωση
              </button>
              <button
                onClick={proceedWithGrant}
                className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
              >
                Συνέχεια Ούτως ή Άλλως
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageDaysView;